import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ConfirmDialog } from '../../components/common/ConfirmDialog.js';
import { useNavigate } from 'react-router-dom';
import type {
  OpenRoleDetail,
  WorkspaceMember,
  HireType,
  OnboardingDocTemplateRecord,
} from '@onboarding-dashboard/shared';
import { rolesService } from '../../services/roles.js';
import { adminService } from '../../services/admin.js';
import { onboardingDocTemplatesService } from '../../services/onboardingDocTemplates.js';
import { useAuth } from '../../contexts/AuthContext.js';
import rolesTemplate from './roles.html?raw';
import './roles.css';

export function RolesPage(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManageRoles = hasRole('admin') || hasRole('hr') || hasRole('manager');
  const isAdminOrHr = hasRole('admin') || hasRole('hr');

  // ─── Shared State ─────────────────────────────────────
  const [activeTab, setActiveTab] = useState('roles');

  // ─── Roles State ──────────────────────────────────────
  const [roles, setRoles] = useState<OpenRoleDetail[]>([]);
  const [activeMembers, setActiveMembers] = useState<WorkspaceMember[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);

  // ─── Documents State ──────────────────────────────────
  const [templates, setTemplates] = useState<OnboardingDocTemplateRecord[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [docStatusFilter, setDocStatusFilter] = useState('true');
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);

  // Refs to avoid stale closures in event handlers
  const templatesRef = useRef(templates);
  templatesRef.current = templates;
  const editingTemplateIdRef = useRef(editingTemplateId);
  editingTemplateIdRef.current = editingTemplateId;
  const rolesRef = useRef(roles);
  rolesRef.current = roles;

  // ─── Helpers ───────────────────────────────────────────

  const showMessage = useCallback((type: 'error' | 'success', message: string) => {
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLDivElement>(`#roles-${type}`);
    const msgEl = container.querySelector<HTMLSpanElement>(`#roles-${type}-message`);
    if (el && msgEl) {
      msgEl.textContent = message;
      el.style.display = 'block';
      if (type === 'success')
        setTimeout(() => {
          el.style.display = 'none';
        }, 4000);
    }
    const other = type === 'error' ? 'success' : 'error';
    const otherEl = container.querySelector<HTMLDivElement>(`#roles-${other}`);
    if (otherEl) otherEl.style.display = 'none';
  }, []);

  // ═══════════════════════════════════════════════════════
  // ROLES TAB
  // ═══════════════════════════════════════════════════════

  const loadRoles = useCallback(async () => {
    setIsLoadingRoles(true);
    const result = await rolesService.list({
      status: statusFilter || undefined,
    });
    if (result.success && result.data) {
      setRoles(result.data.items);
    } else {
      showMessage('error', result.error?.message ?? 'Failed to load roles');
    }
    setIsLoadingRoles(false);
  }, [statusFilter, showMessage]);

  useEffect(() => {
    void loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    async function fetchMembers() {
      const result = await adminService.listMembers();
      if (result.success && result.data) {
        const active = result.data.filter(
          (m) => m.dashboardUserId && m.dashboardStatus === 'active'
        );
        setActiveMembers(active);
      }
    }
    void fetchMembers();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const select = container.querySelector<HTMLSelectElement>('#role-reports-to');
    if (!select) return;
    while (select.options.length > 1) {
      select.remove(1);
    }
    for (const member of activeMembers) {
      const opt = document.createElement('option');
      opt.value = member.dashboardUserId!;
      opt.textContent = `${member.firstName} ${member.lastName}`;
      select.appendChild(opt);
    }
  }, [activeMembers]);

  // Render roles table
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const tbody = container.querySelector<HTMLTableSectionElement>('#roles-table-body');
    const loading = container.querySelector<HTMLDivElement>('#roles-loading');
    const empty = container.querySelector<HTMLDivElement>('#roles-empty');
    const table = container.querySelector<HTMLTableElement>('#roles-table');

    if (isLoadingRoles) {
      if (loading) loading.style.display = '';
      if (empty) empty.style.display = 'none';
      if (table) table.style.display = 'none';
      return;
    }

    if (loading) loading.style.display = 'none';

    if (roles.length === 0) {
      if (empty) empty.style.display = '';
      if (table) table.style.display = 'none';
      return;
    }

    if (empty) empty.style.display = 'none';
    if (table) table.style.display = '';

    if (tbody) {
      tbody.innerHTML = roles
        .map((r) => {
          const reportsToName = r.reportsTo
            ? `${escapeHtml(r.reportsTo.firstName)} ${escapeHtml(r.reportsTo.lastName)}`
            : '—';
          const candidateCount =
            'candidateCount' in r
              ? (r as OpenRoleDetail & { candidateCount: number }).candidateCount
              : 0;
          return `<tr>
            <td><span class="role-title-link" data-role-id="${r.id}">${escapeHtml(r.title)}</span></td>
            <td>${escapeHtml(r.department)}</td>
            <td>${reportsToName}</td>
            <td>${candidateCount}</td>
            <td><span class="role-status role-status--${r.status}">${capitalize(r.status)}</span></td>
            <td>
              <div class="action-group">
                ${canManageRoles ? `<button class="btn-edit" data-edit-id="${r.id}">Edit</button>` : ''}
              </div>
            </td>
          </tr>`;
        })
        .join('');
    }
  }, [roles, isLoadingRoles, canManageRoles]);

  // Role modal helpers
  const openRoleModal = useCallback(
    (roleToEdit?: OpenRoleDetail) => {
      const container = containerRef.current;
      if (!container) return;

      const modal = container.querySelector<HTMLDivElement>('#role-modal');
      const title = container.querySelector<HTMLHeadingElement>('#modal-title');
      const submitBtn = container.querySelector<HTMLButtonElement>('#btn-modal-submit');
      const statusGroup = container.querySelector<HTMLDivElement>('#status-group');
      const formError = container.querySelector<HTMLDivElement>('#form-error');

      if (formError) formError.style.display = 'none';

      const copyNotifsGroup = container.querySelector<HTMLDivElement>('#copy-notifs-group');

      if (roleToEdit) {
        setEditingRoleId(roleToEdit.id);
        if (title) title.textContent = 'Edit Open Role';
        if (submitBtn) submitBtn.textContent = 'Save Changes';
        if (statusGroup) statusGroup.style.display = '';
        if (copyNotifsGroup) copyNotifsGroup.style.display = 'none';
        container.querySelector<HTMLInputElement>('#role-title')!.value = roleToEdit.title;
        container.querySelector<HTMLInputElement>('#role-department')!.value =
          roleToEdit.department;
        container.querySelector<HTMLSelectElement>('#role-reports-to')!.value =
          roleToEdit.reportsToId;
        container.querySelector<HTMLTextAreaElement>('#role-description')!.value =
          roleToEdit.description ?? '';
        container.querySelector<HTMLTextAreaElement>('#role-requirements')!.value =
          roleToEdit.requirements ?? '';
        container.querySelector<HTMLSelectElement>('#role-status')!.value = roleToEdit.status;
        container.querySelector<HTMLSelectElement>('#role-hire-type')!.value =
          roleToEdit.hireType ?? 'regular_hire';
      } else {
        setEditingRoleId(null);
        if (title) title.textContent = 'New Open Role';
        if (submitBtn) submitBtn.textContent = 'Create Role';
        if (statusGroup) statusGroup.style.display = 'none';
        if (copyNotifsGroup) copyNotifsGroup.style.display = '';
        container.querySelector<HTMLFormElement>('#role-form')!.reset();
      }

      // Populate copy-notifs dropdown with existing roles
      const copySelect = container.querySelector<HTMLSelectElement>('#copy-notifs-from');
      if (copySelect) {
        // Keep the first "None" option, rebuild the rest
        while (copySelect.options.length > 1) copySelect.remove(1);
        for (const r of roles) {
          const opt = document.createElement('option');
          opt.value = r.id;
          opt.textContent = r.title;
          copySelect.appendChild(opt);
        }
      }

      if (modal) modal.style.display = '';
    },
    [roles]
  );

  const closeRoleModal = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const modal = container.querySelector<HTMLDivElement>('#role-modal');
    if (modal) modal.style.display = 'none';
    setEditingRoleId(null);
  }, []);

  const handleRoleFormSubmit = useCallback(
    async (e: Event) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const formError = container.querySelector<HTMLDivElement>('#form-error');
      if (formError) formError.style.display = 'none';

      const title = container.querySelector<HTMLInputElement>('#role-title')!.value.trim();
      const department = container
        .querySelector<HTMLInputElement>('#role-department')!
        .value.trim();
      const reportsToId = container.querySelector<HTMLSelectElement>('#role-reports-to')!.value;
      const hireType = container.querySelector<HTMLSelectElement>('#role-hire-type')!.value;
      const description = container
        .querySelector<HTMLTextAreaElement>('#role-description')!
        .value.trim();
      const requirements = container
        .querySelector<HTMLTextAreaElement>('#role-requirements')!
        .value.trim();

      if (editingRoleId) {
        const status = container.querySelector<HTMLSelectElement>('#role-status')!.value;
        const result = await rolesService.update(editingRoleId, {
          title,
          department,
          reportsToId,
          hireType: hireType as HireType,
          description: description || undefined,
          requirements: requirements || undefined,
          status: status as OpenRoleDetail['status'],
        });
        if (result.success) {
          showMessage('success', 'Role updated');
          closeRoleModal();
          void loadRoles();
        } else {
          if (formError) {
            formError.textContent = result.error?.message ?? 'Failed to update role';
            formError.style.display = '';
          }
        }
      } else {
        const copyNotifsFrom =
          container.querySelector<HTMLSelectElement>('#copy-notifs-from')?.value || '';
        const result = await rolesService.create({
          title,
          department,
          reportsToId,
          hireType: hireType as HireType,
          description: description || undefined,
          requirements: requirements || undefined,
        });
        if (result.success) {
          // Copy notification settings from source role if selected
          if (copyNotifsFrom && result.data?.id) {
            try {
              await adminService.copyStageNotificationConfigs(copyNotifsFrom, result.data.id);
              showMessage('success', 'Role created with notification settings');
            } catch {
              showMessage('success', 'Role created (failed to copy notification settings)');
            }
          } else {
            showMessage('success', 'Role created');
          }
          closeRoleModal();
          void loadRoles();
        } else {
          if (formError) {
            formError.textContent = result.error?.message ?? 'Failed to create role';
            formError.style.display = '';
          }
        }
      }
    },
    [editingRoleId, showMessage, closeRoleModal, loadRoles]
  );

  // ═══════════════════════════════════════════════════════
  // DOCUMENTS TAB
  // ═══════════════════════════════════════════════════════

  const loadTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    const isActive = docStatusFilter === '' ? undefined : docStatusFilter === 'true';
    const result = await onboardingDocTemplatesService.list({ isActive });
    if (result.success && result.data) {
      setTemplates(result.data);
    } else {
      showMessage('error', result.error?.message ?? 'Failed to load templates');
    }
    setIsLoadingTemplates(false);
  }, [docStatusFilter, showMessage]);

  useEffect(() => {
    if (isAdminOrHr) {
      void loadTemplates();
    }
  }, [loadTemplates, isAdminOrHr]);

  // Render documents table
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const tbody = container.querySelector<HTMLTableSectionElement>('#obd-table-body');
    const loading = container.querySelector<HTMLDivElement>('#obd-loading');
    const empty = container.querySelector<HTMLDivElement>('#obd-empty');
    const table = container.querySelector<HTMLTableElement>('#obd-table');

    if (isLoadingTemplates) {
      if (loading) loading.style.display = '';
      if (empty) empty.style.display = 'none';
      if (table) table.style.display = 'none';
      return;
    }

    if (loading) loading.style.display = 'none';

    if (templates.length === 0) {
      if (empty) empty.style.display = '';
      if (table) table.style.display = 'none';
      return;
    }

    if (empty) empty.style.display = 'none';
    if (table) table.style.display = '';

    if (tbody) {
      tbody.innerHTML = templates
        .map((t) => {
          const roleCount = t.openRoles?.length ?? 0;
          const roleTags =
            roleCount > 0
              ? (t.openRoles ?? [])
                  .slice(0, 3)
                  .map((r) => `<span class="obd-role-tag">${escapeHtml(r.openRole.title)}</span>`)
                  .join('') +
                (roleCount > 3 ? `<span class="obd-role-count">+${roleCount - 3} more</span>` : '')
              : '<span class="obd-role-count">None</span>';

          const nameCell = t.driveFileUrl
            ? `<a href="${escapeHtml(t.driveFileUrl)}" target="_blank" rel="noopener" class="obd-drive-link">${escapeHtml(t.name)}</a>`
            : escapeHtml(t.name);

          return `<tr>
            <td>${nameCell}</td>
            <td>${t.description ? escapeHtml(t.description) : '<span class="obd-role-count">—</span>'}</td>
            <td>${t.department ? escapeHtml(t.department) : '<span class="obd-role-count">—</span>'}</td>
            <td><div class="obd-role-tags">${roleTags}</div></td>
            <td><span class="obd-status obd-status--${t.isActive ? 'active' : 'inactive'}">${t.isActive ? 'Active' : 'Inactive'}</span></td>
            <td>
              <div class="action-group">
                <button class="btn-edit" data-tpl-edit-id="${t.id}">Edit</button>
                <button class="btn-edit btn-edit--danger" data-tpl-delete-id="${t.id}">Delete</button>
              </div>
            </td>
          </tr>`;
        })
        .join('');
    }
  }, [templates, isLoadingTemplates]);

  // Template modal helpers
  const openTemplateModal = useCallback((templateToEdit?: OnboardingDocTemplateRecord) => {
    const container = containerRef.current;
    if (!container) return;

    const modal = container.querySelector<HTMLDivElement>('#template-modal');
    const title = container.querySelector<HTMLHeadingElement>('#tpl-modal-title');
    const submitBtn = container.querySelector<HTMLButtonElement>('#btn-tpl-modal-submit');
    const statusGroup = container.querySelector<HTMLDivElement>('#tpl-status-group');
    const formError = container.querySelector<HTMLDivElement>('#tpl-form-error');

    if (formError) formError.style.display = 'none';

    // Populate roles checklist
    const checklist = container.querySelector<HTMLDivElement>('#tpl-roles-checklist');
    if (checklist) {
      const assignedRoleIds = new Set((templateToEdit?.openRoles ?? []).map((r) => r.openRoleId));
      const currentRoles = rolesRef.current;
      if (currentRoles.length === 0) {
        checklist.innerHTML = '<p class="roles-checklist-empty">No open roles available.</p>';
      } else {
        checklist.innerHTML = currentRoles
          .map(
            (role) => `
          <div class="roles-checklist-item">
            <input type="checkbox" id="tpl-assign-role-${role.id}" value="${role.id}" ${assignedRoleIds.has(role.id) ? 'checked' : ''} />
            <label for="tpl-assign-role-${role.id}">
              <span class="role-label-title">${escapeHtml(role.title)}</span>
              <span class="role-label-dept">${escapeHtml(role.department)}</span>
            </label>
          </div>`
          )
          .join('');
      }
    }

    const currentFileGroup = container.querySelector<HTMLDivElement>('#current-file-group');
    const currentFileLink = container.querySelector<HTMLAnchorElement>('#current-file-link');
    const fileLabel = container.querySelector<HTMLLabelElement>('#template-file-label');
    const fileHint = container.querySelector<HTMLParagraphElement>('#template-file-hint');

    if (templateToEdit) {
      setEditingTemplateId(templateToEdit.id);
      if (title) title.textContent = 'Edit Template';
      if (submitBtn) submitBtn.textContent = 'Save Changes';
      if (statusGroup) statusGroup.style.display = '';
      container.querySelector<HTMLInputElement>('#template-name')!.value = templateToEdit.name;
      container.querySelector<HTMLTextAreaElement>('#template-description')!.value =
        templateToEdit.description ?? '';
      container.querySelector<HTMLInputElement>('#template-department')!.value =
        templateToEdit.department ?? '';
      container.querySelector<HTMLSelectElement>('#template-status')!.value = String(
        templateToEdit.isActive
      );
      // Show current file info if one exists
      if (templateToEdit.driveFileUrl && currentFileGroup && currentFileLink) {
        currentFileLink.href = templateToEdit.driveFileUrl;
        currentFileLink.textContent = templateToEdit.driveFileName ?? templateToEdit.name;
        currentFileGroup.style.display = '';
      } else if (currentFileGroup) {
        currentFileGroup.style.display = 'none';
      }
      if (fileLabel) fileLabel.textContent = 'Replace file (optional)';
      if (fileHint)
        fileHint.textContent = 'Upload a new file to replace the current one in Google Drive.';
    } else {
      setEditingTemplateId(null);
      if (title) title.textContent = 'New Template';
      if (submitBtn) submitBtn.textContent = 'Create Template';
      if (statusGroup) statusGroup.style.display = 'none';
      if (currentFileGroup) currentFileGroup.style.display = 'none';
      if (fileLabel) fileLabel.textContent = 'Upload file';
      if (fileHint) fileHint.textContent = 'Upload a document to Google Drive (PDF, DOCX, etc.)';
      container.querySelector<HTMLFormElement>('#template-form')!.reset();
    }

    if (submitBtn) {
      submitBtn.disabled = false;
    }
    if (modal) modal.style.display = '';
  }, []);

  const closeTemplateModal = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const modal = container.querySelector<HTMLDivElement>('#template-modal');
    if (modal) modal.style.display = 'none';
    setEditingTemplateId(null);
  }, []);

  const handleTemplateFormSubmit = useCallback(
    async (e: Event) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const submitBtn = container.querySelector<HTMLButtonElement>('#btn-tpl-modal-submit');
      if (submitBtn?.disabled) return;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = editingTemplateId ? 'Saving…' : 'Creating…';
      }

      const formError = container.querySelector<HTMLDivElement>('#tpl-form-error');
      if (formError) formError.style.display = 'none';

      const name = container.querySelector<HTMLInputElement>('#template-name')!.value.trim();
      const description = container
        .querySelector<HTMLTextAreaElement>('#template-description')!
        .value.trim();
      const department = container
        .querySelector<HTMLInputElement>('#template-department')!
        .value.trim();
      const fileInput = container.querySelector<HTMLInputElement>('#template-file')!;
      const file = fileInput.files?.[0];

      const checklist = container.querySelector<HTMLDivElement>('#tpl-roles-checklist');
      const checkedRoleIds = Array.from(
        checklist?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked') ?? []
      ).map((cb) => cb.value);

      const resetBtn = (label: string): void => {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = label;
        }
      };
      const showFormError = (msg: string): void => {
        if (formError) {
          formError.textContent = msg;
          formError.style.display = '';
        }
      };

      const currentEditingId = editingTemplateIdRef.current;
      if (currentEditingId) {
        const isActive =
          container.querySelector<HTMLSelectElement>('#template-status')!.value === 'true';
        const result = await onboardingDocTemplatesService.update(
          currentEditingId,
          {
            name,
            description: description || undefined,
            department: department || undefined,
            isActive,
          },
          file
        );
        if (!result.success) {
          showFormError(result.error?.message ?? 'Failed to update template');
          resetBtn('Save Changes');
          return;
        }
        const assignResult = await onboardingDocTemplatesService.assignRoles(currentEditingId, {
          openRoleIds: checkedRoleIds,
        });
        if (!assignResult.success) {
          showFormError(
            assignResult.error?.message ?? 'Template saved but failed to update role assignments'
          );
          resetBtn('Save Changes');
          return;
        }
        showMessage('success', 'Template updated');
        closeTemplateModal();
        void loadTemplates();
      } else {
        const result = await onboardingDocTemplatesService.create(
          { name, description: description || undefined, department: department || undefined },
          file
        );
        if (!result.success || !result.data) {
          showFormError(result.error?.message ?? 'Failed to create template');
          resetBtn('Create Template');
          return;
        }
        const assignResult = await onboardingDocTemplatesService.assignRoles(result.data.id, {
          openRoleIds: checkedRoleIds,
        });
        if (!assignResult.success) {
          showFormError(
            assignResult.error?.message ?? 'Template created but failed to assign roles'
          );
          resetBtn('Create Template');
          return;
        }
        showMessage('success', 'Template created');
        closeTemplateModal();
        void loadTemplates();
      }
    },
    [showMessage, closeTemplateModal, loadTemplates]
  );

  // ═══════════════════════════════════════════════════════
  // TAB SWITCHING
  // ═══════════════════════════════════════════════════════

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const rolesPanel = container.querySelector<HTMLDivElement>('#panel-roles');
    const docsPanel = container.querySelector<HTMLDivElement>('#panel-documents');
    const tabs = container.querySelectorAll<HTMLButtonElement>('.roles-tab');

    // Show/hide documents tab for non-admin/hr
    const docsTab = container.querySelector<HTMLButtonElement>('[data-tab="documents"]');
    if (docsTab) docsTab.style.display = isAdminOrHr ? '' : 'none';

    if (rolesPanel) rolesPanel.style.display = activeTab === 'roles' ? '' : 'none';
    if (docsPanel) docsPanel.style.display = activeTab === 'documents' ? '' : 'none';

    tabs.forEach((tab) => {
      tab.classList.toggle('roles-tab--active', tab.dataset.tab === activeTab);
    });
  }, [activeTab, isAdminOrHr]);

  // ═══════════════════════════════════════════════════════
  // EVENT LISTENERS
  // ═══════════════════════════════════════════════════════

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Back link
    const backLink = container.querySelector<HTMLAnchorElement>('#back-link');
    const handleBackClick = (e: Event): void => {
      e.preventDefault();
      navigate('/');
    };
    backLink?.addEventListener('click', handleBackClick);

    // Tab switching
    const tabContainer = container.querySelector<HTMLDivElement>('.roles-tabs');
    const handleTabClick = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.roles-tab');
      if (btn?.dataset.tab) {
        if (btn.dataset.tab === 'documents' && !isAdminOrHr) return;
        setActiveTab(btn.dataset.tab);
      }
    };
    tabContainer?.addEventListener('click', handleTabClick);

    // ─── Roles tab listeners ────────────────────────────
    const filterSelect = container.querySelector<HTMLSelectElement>('#filter-status');
    const handleFilterChange = (): void => {
      setStatusFilter(filterSelect?.value ?? '');
    };
    filterSelect?.addEventListener('change', handleFilterChange);

    const createBtn = container.querySelector<HTMLButtonElement>('#btn-create-role');
    if (createBtn) createBtn.style.display = canManageRoles ? '' : 'none';
    const handleCreate = (): void => openRoleModal();
    createBtn?.addEventListener('click', handleCreate);

    const closeBtn = container.querySelector<HTMLButtonElement>('#btn-modal-close');
    const cancelBtn = container.querySelector<HTMLButtonElement>('#btn-modal-cancel');
    closeBtn?.addEventListener('click', closeRoleModal);
    cancelBtn?.addEventListener('click', closeRoleModal);

    const roleForm = container.querySelector<HTMLFormElement>('#role-form');
    roleForm?.addEventListener('submit', handleRoleFormSubmit);

    const tbody = container.querySelector<HTMLTableSectionElement>('#roles-table-body');
    const handleRolesTableClick = (e: Event): void => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('btn-edit')) {
        const roleId = target.dataset.editId;
        const role = roles.find((r) => r.id === roleId);
        if (role) openRoleModal(role);
        return;
      }
      if (target.classList.contains('role-title-link')) {
        const roleId = target.dataset.roleId;
        if (roleId) navigate(`/pipeline?roleId=${roleId}`);
      }
    };
    tbody?.addEventListener('click', handleRolesTableClick);

    // ─── Documents tab listeners ────────────────────────
    const docFilterSelect = container.querySelector<HTMLSelectElement>('#filter-doc-status');
    const handleDocFilterChange = (): void => {
      setDocStatusFilter(docFilterSelect?.value ?? '');
    };
    docFilterSelect?.addEventListener('change', handleDocFilterChange);

    const createTplBtn = container.querySelector<HTMLButtonElement>('#btn-create-template');
    const handleCreateTpl = (): void => openTemplateModal();
    createTplBtn?.addEventListener('click', handleCreateTpl);

    const closeTplBtn = container.querySelector<HTMLButtonElement>('#btn-tpl-modal-close');
    const cancelTplBtn = container.querySelector<HTMLButtonElement>('#btn-tpl-modal-cancel');
    closeTplBtn?.addEventListener('click', closeTemplateModal);
    cancelTplBtn?.addEventListener('click', closeTemplateModal);

    const tplForm = container.querySelector<HTMLFormElement>('#template-form');
    tplForm?.addEventListener('submit', handleTemplateFormSubmit);

    const obdTbody = container.querySelector<HTMLTableSectionElement>('#obd-table-body');
    const handleDocsTableClick = async (e: Event): Promise<void> => {
      const target = e.target as HTMLElement;
      if (target.dataset.tplEditId) {
        const tpl = templatesRef.current.find((t) => t.id === target.dataset.tplEditId);
        if (tpl) openTemplateModal(tpl);
        return;
      }
      if (target.dataset.tplDeleteId) {
        const tpl = templatesRef.current.find((t) => t.id === target.dataset.tplDeleteId);
        if (tpl) setDeleteConfirm({ id: tpl.id, name: tpl.name });
      }
    };
    obdTbody?.addEventListener('click', handleDocsTableClick);

    return () => {
      backLink?.removeEventListener('click', handleBackClick);
      tabContainer?.removeEventListener('click', handleTabClick);
      filterSelect?.removeEventListener('change', handleFilterChange);
      createBtn?.removeEventListener('click', handleCreate);
      closeBtn?.removeEventListener('click', closeRoleModal);
      cancelBtn?.removeEventListener('click', closeRoleModal);
      roleForm?.removeEventListener('submit', handleRoleFormSubmit);
      tbody?.removeEventListener('click', handleRolesTableClick);
      docFilterSelect?.removeEventListener('change', handleDocFilterChange);
      createTplBtn?.removeEventListener('click', handleCreateTpl);
      closeTplBtn?.removeEventListener('click', closeTemplateModal);
      cancelTplBtn?.removeEventListener('click', closeTemplateModal);
      tplForm?.removeEventListener('submit', handleTemplateFormSubmit);
      obdTbody?.removeEventListener('click', handleDocsTableClick);
    };
  }, [
    navigate,
    isAdminOrHr,
    canManageRoles,
    openRoleModal,
    closeRoleModal,
    handleRoleFormSubmit,
    roles,
    openTemplateModal,
    closeTemplateModal,
    handleTemplateFormSubmit,
    showMessage,
    loadTemplates,
  ]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirm) return;
    const result = await onboardingDocTemplatesService.remove(deleteConfirm.id);
    setDeleteConfirm(null);
    if (result.success) {
      showMessage('success', 'Template deleted');
      void loadTemplates();
    } else {
      showMessage('error', result.error?.message ?? 'Failed to delete template');
    }
  }, [deleteConfirm, showMessage, loadTemplates]);

  return (
    <>
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: rolesTemplate }} />
      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        title="Delete template"
        message={`Delete "${deleteConfirm?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setDeleteConfirm(null)}
      />
    </>
  );
}

// ─── Utility ──────────────────────────────────────────

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
